/**
 * Verification helper utility for validating element values against regex patterns.
 * Based on SwingVerifyHelper from the Java Swing client.
 */

import { collectFormLayoutElements } from '@/common/utils/validateFormElementNames'
import { failsTextFieldFormatVerify } from '@/common/utils/textFieldFormat'

export interface VerifyResult {
  isValid: boolean
  errorMessage?: string
}

/** Java Pattern inline flags at pattern start, e.g. `(?s).+` from meta verify_re. */
function compileVerifyRegexp(regexpPattern: string): RegExp {
  let pattern = regexpPattern
  let flags = ''

  const inlineFlags = pattern.match(/^\(\?([a-z-]+)\)/i)
  if (inlineFlags) {
    const f = inlineFlags[1]
    if (f.includes('i')) flags += 'i'
    if (f.includes('m')) flags += 'm'
    if (f.includes('s')) flags += 's'
    if (f.includes('u')) flags += 'u'
    pattern = pattern.slice(inlineFlags[0].length)
  }

  return new RegExp(pattern, flags)
}

/**
 * Validates text against a regex pattern.
 *
 * @param value - The value to validate
 * @param regexpPattern - The regex pattern to match against
 * @param errorMessage - Optional error message to return on validation failure
 * @returns VerifyResult object with validation status and optional error message
 */
export function verifyValue(
  value: string,
  regexpPattern: string,
  errorMessage?: string
): VerifyResult {
  // If no regexp or explicitly disabled, always pass
  if (!regexpPattern || regexpPattern === 'false' || regexpPattern.trim() === '') {
    return { isValid: true }
  }

  try {
    // Normalize line breaks (replace \r\n with \n) like Swing client does
    const normalizedValue = value.replace(/\r\n/g, '\n')

    const pattern = compileVerifyRegexp(regexpPattern)
    const matches = pattern.test(normalizedValue)

    if (!matches) {
      return {
        isValid: false,
        errorMessage: errorMessage || 'Validation failed'
      }
    }

    return { isValid: true }
  } catch (error) {
    console.error('Error in regex validation:', error, 'Pattern:', regexpPattern)
    return {
      isValid: false,
      errorMessage: 'Invalid validation pattern'
    }
  }
}

/**
 * Checks if a value matches a regex pattern (returns boolean only).
 *
 * @param value - The value to validate
 * @param regexpPattern - The regex pattern to match against
 * @returns true if validation passed, false otherwise
 */
export function matches(value: string, regexpPattern: string): boolean {
  const result = verifyValue(value, regexpPattern)
  return result.isValid
}

/**
 * Validates multiple values at once.
 *
 * @param validations - Array of validation configs
 * @returns Array of VerifyResult objects
 */
export function verifyMultiple(
  validations: Array<{
    value: string
    regexpPattern: string
    errorMessage?: string
  }>
): VerifyResult[] {
  return validations.map(config =>
    verifyValue(config.value, config.regexpPattern, config.errorMessage)
  )
}

/**
 * Verifies all elements in a registry.
 * Similar to VerifyHelper.verifyContainer() in Swing client.
 *
 * @param elements - Registry of elements to verify
 * @param filterFn - Optional filter function to select which elements to verify
 * @returns true if all elements pass verification, false otherwise
 */
export type VerifyFormElementsResult = {
  valid: boolean
  firstInvalidName?: string
}

function resolveRegistryElementName(registryKey: string, element: Record<string, unknown>): string {
  const fieldName = String(element.fieldName ?? '').trim()
  return fieldName || registryKey
}

export type ActionLayoutVerifyScope = {
  level: string
  id?: string | number
  panelId?: string | number
  elementId?: string | number
}

export type VerifyFormElementsOptions = {
  layoutScope?: ActionLayoutVerifyScope
  formRoot?: unknown
  /** Slot/registry aliases for modal SAME_WINDOW (e.g. schema 2581 in slot 506). */
  formIdAliases?: string[]
}

function resolveFormLayoutRoot(formRoot: unknown): Record<string, unknown> | null {
  if (!formRoot || typeof formRoot !== 'object') return null
  const root = formRoot as Record<string, unknown>
  if (root.panels != null || root.actions != null || root.main != null) {
    return root
  }
  const nested = root.form
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return root
}

function tryParseElementId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseInt(value.trim(), 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function readElementConfigId(config: Record<string, unknown>): number | null {
  const attrs = config._attributes as { id?: unknown } | undefined
  return tryParseElementId(attrs?.id ?? config.id)
}

function collectElementIdsInSubtree(node: unknown, out: Set<number>): void {
  if (!node || typeof node !== 'object') return

  if (Array.isArray(node)) {
    for (const item of node) {
      collectElementIdsInSubtree(item, out)
    }
    return
  }

  const record = node as Record<string, unknown>
  const directElement = record.element
  if (directElement && typeof directElement === 'object') {
    if (Array.isArray(directElement)) {
      for (const nested of directElement) {
        collectElementIdsInSubtree(nested, out)
      }
    } else {
      const nestedId = readElementConfigId(directElement as Record<string, unknown>)
      if (nestedId != null) {
        out.add(nestedId)
      }
      collectElementIdsInSubtree(directElement, out)
    }
  }

  for (const value of Object.values(record)) {
    if (value && typeof value === 'object') {
      collectElementIdsInSubtree(value, out)
    }
  }
}

function findElementConfigById(formRoot: unknown, elementId: number): Record<string, unknown> | null {
  const layout = resolveFormLayoutRoot(formRoot)
  const panelsMap = layout?.panels
  if (!panelsMap || typeof panelsMap !== 'object' || Array.isArray(panelsMap)) {
    return null
  }

  for (const panelData of Object.values(panelsMap as Record<string, unknown>)) {
    if (!panelData || typeof panelData !== 'object') continue
    const rows = (panelData as Record<string, unknown>).rows
    if (!Array.isArray(rows)) continue

    for (const rowItem of rows) {
      if (!Array.isArray(rowItem)) continue
      for (const cellItem of rowItem) {
        const elementConfig = (cellItem as { element?: Record<string, unknown> })?.element
        if (!elementConfig || typeof elementConfig !== 'object') continue
        if (readElementConfigId(elementConfig) === elementId) {
          return elementConfig
        }
      }
    }
  }

  return null
}

function collectPagePanelIds(formRoot: unknown, pageId: string | number): Set<string> {
  const ids = new Set<string>()
  const pageIdStr = String(pageId)

  const extractPanelsFromPage = (page: unknown) => {
    if (!page || typeof page !== 'object') return
    const pageRecord = page as Record<string, unknown>
    const currentPageId = pageRecord.id ?? (pageRecord._attributes as { id?: unknown } | undefined)?.id
    if (String(currentPageId) !== pageIdStr) return

    const rows = pageRecord.panelRows
    if (!Array.isArray(rows)) return
    for (const row of rows) {
      if (!Array.isArray(row)) continue
      for (const cell of row) {
        if (!cell || typeof cell !== 'object') continue
        const cellRecord = cell as Record<string, unknown>
        const panelId = cellRecord.panelId
          ?? (cellRecord._attributes as { id?: unknown } | undefined)?.id
          ?? cellRecord.id
        if (panelId != null && String(panelId).trim()) {
          ids.add(String(panelId))
        }
      }
    }
  }

  const form = resolveFormLayoutRoot(formRoot)
  if (!form) return ids

  for (const regionKey of ['main', 'header', 'footer'] as const) {
    const region = form[regionKey]
    if (!Array.isArray(region)) continue
    for (const band of region) {
      if (Array.isArray(band)) {
        for (const page of band) {
          extractPanelsFromPage(page)
        }
      } else {
        extractPanelsFromPage(band)
      }
    }
  }

  return ids
}

/** Layout action scope for verify: level + id from action XML / ActionContext. */
export function resolveActionLayoutVerifyScope(input: {
  level?: string
  layout?: {
    level?: { _text?: string }
    id?: { _text?: string } | string | number
  }
  formId?: string | number
  panelId?: string | number
  elementId?: string | number
  pageId?: string | number
}): ActionLayoutVerifyScope {
  const layout = input.layout ?? {}
  const level = String(layout.level?._text ?? input.level ?? 'form').toLowerCase()
  const rawId = layout.id
  let id: string | number | undefined
  if (rawId != null && typeof rawId === 'object' && '_text' in rawId) {
    id = rawId._text
  } else {
    id = rawId as string | number | undefined
  }

  if (id == null || id === '') {
    switch (level) {
      case 'panel':
        id = input.panelId
        break
      case 'element':
        id = input.elementId
        break
      case 'page':
        id = input.pageId
        break
      case 'form':
        id = input.formId
        break
    }
  }

  return {
    level,
    id,
    panelId: input.panelId,
    elementId: input.elementId,
  }
}

/** Element ids to verify for the given action layout; null = entire form. */
export function collectLayoutScopedVerifyElementIds(
  formRoot: unknown,
  scope: ActionLayoutVerifyScope,
): Set<number> | null {
  const level = String(scope.level ?? 'form').toLowerCase()
  if (level === 'form') {
    return null
  }

  if (level === 'element') {
    const rootId = tryParseElementId(scope.id ?? scope.elementId)
    if (rootId == null) {
      return new Set()
    }

    const ids = new Set<number>([rootId])
    const elementConfig = findElementConfigById(formRoot, rootId)
    if (elementConfig) {
      collectElementIdsInSubtree(elementConfig, ids)
    }
    return ids
  }

  if (level === 'panel') {
    const panelId = String(scope.id ?? scope.panelId ?? '')
    const ids = new Set<number>()
    for (const element of collectFormLayoutElements(formRoot)) {
      if (element.panelId === panelId && element.id != null) {
        ids.add(element.id)
      }
    }
    return ids
  }

  if (level === 'page') {
    const pagePanelIds = collectPagePanelIds(formRoot, scope.id ?? '')
    const ids = new Set<number>()
    for (const element of collectFormLayoutElements(formRoot)) {
      if (element.panelId && pagePanelIds.has(element.panelId) && element.id != null) {
        ids.add(element.id)
      }
    }
    return ids
  }

  return null
}

function buildWantedFormIdSet(wantedFormId: string, aliases?: string[]): Set<string> {
  const ids = new Set<string>()
  const primary = String(wantedFormId ?? '').trim()
  if (primary) ids.add(primary)
  for (const alias of aliases ?? []) {
    const normalized = String(alias ?? '').trim()
    if (normalized) ids.add(normalized)
  }
  return ids
}

function elementMatchesWantedForm(
  element: Record<string, unknown>,
  wantedFormIds: Set<string>,
): boolean {
  if (wantedFormIds.size === 0) return true
  const candidates = [
    element.formId,
    element.pageDataSlot,
    (element.ctx as { formPath?: { formId?: unknown } } | undefined)?.formPath?.formId,
  ]
  for (const candidate of candidates) {
    const normalized = String(candidate ?? '').trim()
    if (normalized && wantedFormIds.has(normalized)) {
      return true
    }
  }
  return false
}

/** Panel/element layout scope: verify by container id (modal slot may differ from schema formId). */
function elementMatchesVerifyFormFilter(
  element: Record<string, unknown>,
  wantedFormIds: Set<string>,
  layoutScope: ActionLayoutVerifyScope | undefined,
): boolean {
  const level = String(layoutScope?.level ?? 'form').toLowerCase()
  if (level === 'panel') {
    const panelId = String(layoutScope?.id ?? layoutScope?.panelId ?? '')
    if (panelId && String(element.panelId ?? '') === panelId) {
      return true
    }
  }
  if (level === 'element') {
    const elementId = String(layoutScope?.id ?? layoutScope?.elementId ?? '')
    if (elementId && String(element.elementId ?? '') === elementId) {
      return true
    }
  }
  return elementMatchesWantedForm(element, wantedFormIds)
}

function shouldVerifyElementInLayoutScope(
  element: Record<string, unknown>,
  scope: ActionLayoutVerifyScope | undefined,
  scopedElementIds: Set<number> | null | undefined,
  hasFormRoot: boolean,
): boolean {
  const level = String(scope?.level ?? 'form').toLowerCase()
  if (!scope || level === 'form') {
    return true
  }

  if (hasFormRoot && scopedElementIds && scopedElementIds.size > 0) {
    const elementId = tryParseElementId(element.elementId)
    if (elementId != null && scopedElementIds.has(elementId)) {
      return true
    }
    // Modal/slot remount: registry may have panelId but stale/missing elementId.
    if (level === 'panel') {
      return String(element.panelId ?? '') === String(scope.id ?? scope.panelId ?? '')
    }
    if (level === 'element') {
      return String(element.elementId ?? '') === String(scope.id ?? scope.elementId ?? '')
    }
    return false
  }

  switch (level) {
    case 'panel':
      return String(element.panelId ?? '') === String(scope.id ?? scope.panelId ?? '')
    case 'element':
      return String(element.elementId ?? '') === String(scope.id ?? scope.elementId ?? '')
    case 'page':
      return String(element.pageId ?? '') === String(scope.id ?? '')
    default:
      return true
  }
}

function readElementVerifyPattern(element: Record<string, unknown>): string {
  const fromDirect = element.verify_re ?? element.verifyRegexp
  if (typeof fromDirect === 'string') return fromDirect
  if (fromDirect && typeof fromDirect === 'object' && '_text' in fromDirect) {
    return String((fromDirect as { _text?: unknown })._text ?? '')
  }
  const nested = element.element
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const nestedPattern = (nested as Record<string, unknown>).verify_re
    if (typeof nestedPattern === 'string') return nestedPattern
    if (nestedPattern && typeof nestedPattern === 'object' && '_text' in nestedPattern) {
      return String((nestedPattern as { _text?: unknown })._text ?? '')
    }
  }
  return ''
}

function readElementVerifyErrorMessage(element: Record<string, unknown>): string | undefined {
  const fromDirect = element.verify_error
  if (typeof fromDirect === 'string' && fromDirect.trim()) return fromDirect
  if (fromDirect && typeof fromDirect === 'object' && '_text' in fromDirect) {
    const text = String((fromDirect as { _text?: unknown })._text ?? '').trim()
    return text || undefined
  }
  return undefined
}

function readElementLiveValue(element: Record<string, unknown>): unknown {
  const liveReader = element.getLiveFieldValue
  if (typeof liveReader === 'function') {
    try {
      return (liveReader as () => unknown)()
    } catch {
      return undefined
    }
  }
  return undefined
}

function readNestedXmlText(node: unknown): string {
  if (typeof node === 'string') return node
  if (node && typeof node === 'object' && '_text' in node) {
    return String((node as { _text?: unknown })._text ?? '')
  }
  return ''
}

function readElementType(element: Record<string, unknown>): string {
  const direct = readNestedXmlText(element.type)
  if (direct.trim()) return direct.trim().toUpperCase()
  const nested = element.element
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return readNestedXmlText((nested as Record<string, unknown>).type).trim().toUpperCase()
  }
  return ''
}

function readElementFormat(element: Record<string, unknown>): string {
  const direct = readNestedXmlText(element.format)
  if (direct.trim()) return direct.trim()
  const nested = element.element
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return readNestedXmlText((nested as Record<string, unknown>).format).trim()
  }
  return ''
}

function isTextLikeFormatElementType(elementType: string): boolean {
  return elementType === 'TEXT' || elementType === 'TEXTAREA' || elementType === 'MULTILINETEXT'
}

function applyVerifyError(element: Record<string, unknown>, message: string): void {
  if (typeof element.setError !== 'function') return
  try {
    ;(element.setError as (message: string | null) => void)(message)
  } catch {
    // ignore setError failures in verify path
  }
}

function verifyAgainstLiveValue(
  element: Record<string, unknown>,
  verifyPattern: string,
  verifyErrorMessage: string | undefined,
): { applied: boolean; isValid: boolean } {
  const liveValue = readElementLiveValue(element)
  if (liveValue === undefined) {
    return { applied: false, isValid: true }
  }

  const liveStr = String(liveValue ?? '')
  const fallbackMessage = verifyErrorMessage || 'Validation failed'
  let applied = false
  let isValid = true

  if (verifyPattern && verifyPattern !== 'false' && verifyPattern.trim() !== '') {
    applied = true
    const result = verifyValue(liveStr, verifyPattern, verifyErrorMessage)
    if (!result.isValid) {
      isValid = false
      applyVerifyError(element, result.errorMessage || fallbackMessage)
    }
  }

  const elementType = readElementType(element)
  const formatName = readElementFormat(element)
  if (isTextLikeFormatElementType(elementType) && failsTextFieldFormatVerify(formatName, liveStr)) {
    applied = true
    isValid = false
    applyVerifyError(element, fallbackMessage)
  }

  return { applied, isValid }
}

/**
 * Verifies editable elements of a form; returns the first failed field name (for focus).
 * Like Swing `getForm().verify()` / `doVerify()` before `doAction`.
 */
export function verifyFormElements(
  elements: Record<string, unknown>,
  formId: string,
  options?: VerifyFormElementsOptions,
): VerifyFormElementsResult {
  const wantedFormId = String(formId ?? '').trim()
  if (!wantedFormId) {
    return { valid: true }
  }

  const layoutScope = options?.layoutScope
  const hasFormRoot = options?.formRoot != null
  const wantedFormIds = buildWantedFormIdSet(wantedFormId, options?.formIdAliases)
  const scopedElementIds = layoutScope && hasFormRoot
    ? collectLayoutScopedVerifyElementIds(options.formRoot, layoutScope)
    : undefined

  let allValid = true
  let firstInvalidName: string | undefined

  for (const [registryKey, rawElement] of Object.entries(elements)) {
    const element = rawElement as Record<string, unknown>
    if (!elementMatchesVerifyFormFilter(element, wantedFormIds, layoutScope)) {
      continue
    }
    if (!shouldVerifyElementInLayoutScope(element, layoutScope, scopedElementIds, hasFormRoot)) {
      continue
    }

    const verifyPattern = readElementVerifyPattern(element)
    const verifyErrorMessage = readElementVerifyErrorMessage(element)
    const hasVerifyFn = typeof element.verify === 'function'
    const hasTextFormat =
      isTextLikeFormatElementType(readElementType(element)) && Boolean(readElementFormat(element))
    if (!hasVerifyFn && !verifyPattern && !hasTextFormat) {
      continue
    }

    const isValidByElement = hasVerifyFn ? (element.verify as () => boolean)() : true
    const liveVerify = verifyAgainstLiveValue(element, verifyPattern, verifyErrorMessage)
    const isValid = isValidByElement && (!liveVerify.applied || liveVerify.isValid)
    if (!isValid) {
      allValid = false
      if (!firstInvalidName) {
        firstInvalidName = resolveRegistryElementName(registryKey, element)
      }
    }
  }

  return { valid: allValid, firstInvalidName }
}

export function verifyElements(
  elements: Record<string, any>,
  filterFn?: (element: any) => boolean
): boolean {
  let allValid = true

  for (const [elementName, element] of Object.entries(elements)) {
    // Apply filter if provided
    if (filterFn && !filterFn(element)) {
      continue
    }

    // Skip if element doesn't have verify method
    if (typeof element.verify !== 'function') {
      continue
    }

    // Call verify on the element
    const isValid = element.verify()

    if (!isValid) {
      allValid = false
      // Don't break - continue verifying all elements to show all errors
    }
  }

  return allValid
}

/**
 * Common regex patterns for validation.
 */
export const CommonPatterns = {
  EMAIL: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
  PHONE: '^\\+?[1-9]\\d{1,14}$', // E.164 format
  PHONE_RU: '^\\+7\\d{10}$', // Russian phone: +7XXXXXXXXXX
  NUMBER: '^-?\\d+$',
  DECIMAL: '^-?\\d+(\\.\\d+)?$',
  URL: '^(https?:\\/\\/)?(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&//=]*)$',
  DATE_ISO: '^\\d{4}-\\d{2}-\\d{2}$', // YYYY-MM-DD
  TIME: '^([01]?\\d|2[0-3]):[0-5]\\d$', // HH:MM
  POSTAL_CODE_RU: '^\\d{6}$', // Russian postal code
  INN: '^(\\d{10}|\\d{12})$', // Russian tax ID
  NOT_EMPTY: '^.+$',
  ALPHANUMERIC: '^[a-zA-Z0-9]+$',
}

